import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
    changePasswordFormFields,
    changePasswordSchema,
    ChangePasswordType,
} from '@Entities/schemas/user/forgot-password'
import { zodResolver } from '@hookform/resolvers/zod'
import { ICONS } from '@Const/common'
import { StoresContext } from '@App/contexts'
import RootStore from '@App/stores/root-store'
import { useNavigate } from 'react-router'
import Form from '@App/kit/fields/v2/Form'
import { observer } from 'mobx-react'
import { HttpError } from '@App/exceptions/HttpError'
import clsx from 'clsx'

interface ChangePasswordStepProps {
    username?: string | undefined
    setHasBeenUpdated?(newState: boolean): void
}

const ChangePasswordStep = (props: ChangePasswordStepProps) => {
    const navigate = useNavigate()

    const { authStore, popupStore, preloaderStore, userStore } = useContext<RootStore>(StoresContext)

    const { username, setHasBeenUpdated } = props

    const isLoggedIn = !username

    const [badResponse, setBadResponse] = useState<undefined | any>(undefined)

    const methods = useForm<ChangePasswordType>({
        mode: 'onChange',
        criteriaMode: 'all',
        resolver: zodResolver(
            changePasswordSchema.refine((data) => data.code !== badResponse?.code, {
                path: ['code'],
                message: badResponse?.message,
            })
        ),
    })

    const onSubmit = async (data: ChangePasswordType) => {
        preloaderStore.openTransparentPreloader(true)
        try {
            username
                ? await authStore.confirmForgotPassword(username, data.code, data.password)
                : await userStore.changeUserPassword(data.code, data.password)
        } catch (e) {
            preloaderStore.openTransparentPreloader(false)
            if (e instanceof Error || e instanceof HttpError) {
                setBadResponse({ code: data.code, message: e.message })
            } else {
                setBadResponse({ code: data.code, message: 'Something has gone wrong.' })
            }
            return
        }
        popupStore.setAlertData('Password updated!', false)
        preloaderStore.openTransparentPreloader(false)
        setHasBeenUpdated && setHasBeenUpdated(true)
        if (!isLoggedIn) {
            navigate('/login')
        }
    }

    useEffect(() => {
        if (badResponse) {
            methods.trigger().then()
        }
    }, [badResponse, methods.trigger])

    return (
        <>
            <div className={clsx('change-password', isLoggedIn ?? 'logged-in')}>
                <h3>{username ? 'Forgot password' : 'Change Password'}</h3>
                <p>
                    We&apos;ve attempted to email you a password reset code. Please check your email and enter the code
                    below before selecting a new password.
                </p>
                <Form
                    onSubmit={onSubmit}
                    submitButtonLabel="Confirm Password Reset"
                    formFields={changePasswordFormFields}
                    methods={methods}
                    icon={ICONS.arrow_forward}
                    disableSubmit={!methods.formState.isValid}
                />
            </div>
        </>
    )
}

export default observer(ChangePasswordStep)
